import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Select from "react-select";

import CkEditorBlog from "../../elements/CkEditor/CkEditorBlog";
import NoImage from "../../../assets/images/no-image.jpg";
import PageTitle from "../../layouts/PageTitle";
import {
  createBlogApi,
  getBlogByIdApi,
  getBlogCategoriesApi,
  getBlogsApi,
  updateBlogApi,
} from "../../../services/BlogService";

const STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "published", label: "Published" },
  { value: "scheduled", label: "Scheduled" },
];

const LANGUAGE_OPTIONS = [
  { value: "en", label: "English" },
  { value: "ar", label: "Arabic" },
  { value: "ku", label: "Kurdish" },
];

const AUTHOR_OPTIONS = [
  { value: "admin-1", label: "Admin" },
  { value: "editor-1", label: "Editor" },
  { value: "marketing-1", label: "Marketing Team" },
];

const normalizeToken = (value = "") => value.trim().replace(/\s+/g, " ");

const slugify = (value = "") =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const AddBlog = ({ motherMenu = "CMS", forcedBlogId = "" }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const blogId = forcedBlogId || searchParams.get("blogId");
  const isEditMode = Boolean(blogId);

  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    featuredImage: "",
    content: "",
    excerpt: "",
    category: "",
    tags: [],
    authorId: AUTHOR_OPTIONS[0].value,
    authorName: "",
    publishStatus: "draft",
    publishDate: "",
    language: "en",
    isFeatured: false,
    relatedBlogs: [],
  });
  const [isSlugTouched, setIsSlugTouched] = useState(false);
  const [newTag, setNewTag] = useState("");
  const [newCategory, setNewCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [allBlogs, setAllBlogs] = useState([]);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const excerptLength = formData.excerpt.length;

  const relatedBlogOptions = useMemo(
    () =>
      allBlogs
        .filter((item) => item.id !== blogId)
        .map((item) => ({ value: item.id, label: item.title || item.slug })),
    [allBlogs, blogId],
  );

  const authorNameFromSelection = useMemo(() => {
    const selected = AUTHOR_OPTIONS.find(
      (item) => item.value === formData.authorId,
    );
    return selected ? selected.label : "";
  }, [formData.authorId]);

  const updateFormData = (key, value) => {
    setFormData((previous) => ({ ...previous, [key]: value }));
  };

  const handleTitleChange = (event) => {
    const value = event.target.value;
    setFormData((previous) => ({
      ...previous,
      title: value,
      slug: isSlugTouched ? previous.slug : slugify(value),
    }));
  };

  const handleTagAdd = (event) => {
    if (event) {
      event.preventDefault();
    }

    const nextTag = normalizeToken(newTag);
    if (!nextTag) {
      return;
    }

    setFormData((previous) => ({
      ...previous,
      tags: previous.tags.some(
        (item) => item.toLowerCase() === nextTag.toLowerCase(),
      )
        ? previous.tags
        : [...previous.tags, nextTag],
    }));
    setNewTag("");
    setErrorMessage("");
  };

  const handleTagRemove = (tag) => {
    setFormData((previous) => ({
      ...previous,
      tags: previous.tags.filter((item) => item !== tag),
    }));
  };

  const handleCategoryAdd = (event) => {
    if (event) {
      event.preventDefault();
    }

    const nextCategory = normalizeToken(newCategory);
    if (!nextCategory) {
      return;
    }

    setCategories((previous) =>
      previous.some((item) => item.toLowerCase() === nextCategory.toLowerCase())
        ? previous
        : [...previous, nextCategory],
    );
    updateFormData("category", nextCategory);
    setNewCategory("");
    setErrorMessage("");
  };

  const handleImageChange = async (event, fieldName) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      const encodedImage = await fileToDataUrl(file);
      updateFormData(fieldName, encodedImage);
    } catch (error) {
      setErrorMessage("Unable to process selected image.");
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [categoriesResponse, blogsResponse] = await Promise.all([
          getBlogCategoriesApi(),
          getBlogsApi(),
        ]);

        setCategories(
          Array.isArray(categoriesResponse?.data?.data)
            ? categoriesResponse.data.data
            : [],
        );
        setAllBlogs(
          Array.isArray(blogsResponse?.data?.data)
            ? blogsResponse.data.data
            : [],
        );
      } catch (error) {
        setErrorMessage(
          "Unable to load blog categories and related blog data.",
        );
      }
    };

    loadInitialData();
  }, []);

  useEffect(() => {
    if (!isEditMode || !blogId) {
      return;
    }

    const loadBlog = async () => {
      try {
        setIsInitialLoading(true);
        setErrorMessage("");
        const response = await getBlogByIdApi(blogId);
        const blog = response?.data?.data || {};
        setFormData({
          title: blog.title || "",
          slug: blog.slug || "",
          featuredImage: blog.featuredImage || "",
          content: blog.content || "",
          excerpt: blog.excerpt || "",
          category: blog.category || "",
          tags: Array.isArray(blog.tags) ? blog.tags : [],
          authorId: blog.authorId || AUTHOR_OPTIONS[0].value,
          authorName: blog.authorName || "",
          publishStatus: blog.publishStatus || "draft",
          publishDate: blog.publishDate
            ? new Date(blog.publishDate).toISOString().slice(0, 16)
            : "",
          language: blog.language || "en",
          isFeatured: Boolean(blog.isFeatured),
          relatedBlogs: Array.isArray(blog.relatedBlogs)
            ? blog.relatedBlogs
            : [],
        });
        setIsSlugTouched(true);
      } catch (error) {
        setErrorMessage(
          error?.response?.data?.message || "Unable to load blog for editing.",
        );
      } finally {
        setIsInitialLoading(false);
      }
    };

    loadBlog();
  }, [blogId, isEditMode]);

  const handleSubmitBlog = async (statusOverride) => {
    const nextPublishStatus = statusOverride || formData.publishStatus;

    if (!formData.title.trim()) {
      setErrorMessage("Blog title is required.");
      return;
    }

    if (!formData.slug.trim()) {
      setErrorMessage("Slug is required.");
      return;
    }

    if (!formData.content.trim()) {
      setErrorMessage("Blog content is required.");
      return;
    }

    if (!formData.excerpt.trim()) {
      setErrorMessage("Excerpt is required.");
      return;
    }

    if (excerptLength < 150 || excerptLength > 300) {
      setErrorMessage("Excerpt must be between 150 and 300 characters.");
      return;
    }

    if (!formData.category.trim()) {
      setErrorMessage("Category is required.");
      return;
    }

    if (nextPublishStatus === "scheduled" && !formData.publishDate) {
      setErrorMessage("Publish date is required for scheduled posts.");
      return;
    }

    const payload = {
      title: formData.title.trim(),
      slug: formData.slug.trim(),
      featuredImage: formData.featuredImage,
      content: formData.content,
      excerpt: formData.excerpt.trim(),
      category: formData.category.trim(),
      tags: formData.tags,
      authorId: formData.authorId,
      authorName: formData.authorName.trim() || authorNameFromSelection,
      publishStatus: nextPublishStatus,
      publishDate: formData.publishDate || undefined,
      language: formData.language,
      isFeatured: formData.isFeatured,
      relatedBlogs: formData.relatedBlogs,
    };

    try {
      setIsSubmitting(true);
      setErrorMessage("");
      setSuccessMessage("");
      if (isEditMode && blogId) {
        await updateBlogApi(blogId, payload);
      } else {
        await createBlogApi(payload);
      }
      setSuccessMessage(
        nextPublishStatus === "draft"
          ? "Blog saved as draft successfully."
          : `Blog ${isEditMode ? "updated" : "created"} successfully.`,
      );
      navigate("/all-blogs");
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message ||
          `Unable to ${isEditMode ? "update" : "publish"} blog. Please try again.`,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <PageTitle
        activeMenu={isEditMode ? "Edit Blog" : "Add Blog"}
        motherMenu={motherMenu}
      />
      <div className="row mb-3">
        <div className="col-12 d-flex flex-wrap gap-2">
          <Link to="/all-blogs" className="btn btn-sm btn-primary">
            Blog List
          </Link>
          <Link to="/blog-category" className="btn btn-sm btn-outline-primary">
            Blog Categories
          </Link>
        </div>
      </div>

      <div className="row">
        <div className="col-xl-8">
          <div className="card">
            <div className="card-header">
              <h4 className="card-title mb-0">Core Blog Details</h4>
            </div>
            <div className="card-body">
              {isInitialLoading ? (
                <div className="alert alert-info py-2" role="alert">
                  Loading blog details...
                </div>
              ) : null}
              <div className="mb-3">
                <label className="form-label">Blog Title (H1)</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter blog title"
                  value={formData.title}
                  onChange={handleTitleChange}
                />
              </div>

              {/* <div className="mb-3">
                <label className="form-label">Slug / URL</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="blog-url-slug"
                  value={formData.slug}
                  onChange={(event) => {
                    setIsSlugTouched(true);
                    updateFormData("slug", slugify(event.target.value));
                  }}
                />
              </div> */}

              <div className="mb-3">
                <label className="form-label">Blog Content (WYSIWYG)</label>
                <div className="custom-ekeditor cms-radius add-content-ckeditor blog-editor-compact">
                  <CkEditorBlog
                    value={formData.content}
                    onChange={(value) => updateFormData("content", value)}
                  />
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">
                  Excerpt / Short Description
                </label>
                <textarea
                  rows="4"
                  className="form-control"
                  placeholder="150-300 character summary"
                  value={formData.excerpt}
                  onChange={(event) =>
                    updateFormData("excerpt", event.target.value)
                  }
                />
                <div
                  className={`form-text ${excerptLength < 150 || excerptLength > 300 ? "text-danger" : "text-success"}`}
                >
                  {excerptLength}/300 characters (required 150-300)
                </div>
              </div>

              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Category</label>
                  <select
                    className="form-control"
                    value={formData.category}
                    onChange={(event) =>
                      updateFormData("category", event.target.value)
                    }
                  >
                    <option value="">Select Category</option>
                    {categories.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>
                </div>
                {/* <div className="col-md-6">
                  <label className="form-label">Create Category</label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Add new category"
                      value={newCategory}
                      onChange={(event) => setNewCategory(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          handleCategoryAdd(event);
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={(event) => handleCategoryAdd(event)}
                    >
                      Add
                    </button>
                  </div>
                </div> */}
              </div>

              <div className="mt-3">
                <label className="form-label">Tags</label>
                <div className="input-group mb-2">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Type tag and click add"
                    value={newTag}
                    onChange={(event) => setNewTag(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        handleTagAdd(event);
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={(event) => handleTagAdd(event)}
                  >
                    Add Tag
                  </button>
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {formData.tags.map((tag) => (
                    <span key={tag} className="badge badge-primary light">
                      {tag}
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 ms-2 text-danger"
                        onClick={() => handleTagRemove(tag)}
                      >
                        x
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-4">
          <div className="card">
            <div className="card-header">
              <h4 className="card-title mb-0">Publishing</h4>
            </div>
            <div className="card-body">
              <div className="mb-3">
                <label className="form-label">Publish Status</label>
                <select
                  className="form-control"
                  value={formData.publishStatus}
                  onChange={(event) =>
                    updateFormData("publishStatus", event.target.value)
                  }
                >
                  {STATUS_OPTIONS.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-3">
                <label className="form-label">Publish Date & Time</label>
                <input
                  type="datetime-local"
                  className="form-control"
                  value={formData.publishDate}
                  onChange={(event) =>
                    updateFormData("publishDate", event.target.value)
                  }
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Language</label>
                <select
                  className="form-control"
                  value={formData.language}
                  onChange={(event) =>
                    updateFormData("language", event.target.value)
                  }
                >
                  {LANGUAGE_OPTIONS.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-check mb-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="featuredBlogToggle"
                  checked={formData.isFeatured}
                  onChange={(event) =>
                    updateFormData("isFeatured", event.target.checked)
                  }
                />
                <label
                  className="form-check-label"
                  htmlFor="featuredBlogToggle"
                >
                  Mark as Featured Blog
                </label>
              </div>

              <div className="mb-3">
                <label className="form-label">Author</label>
                <select
                  className="form-control"
                  value={formData.authorId}
                  onChange={(event) =>
                    updateFormData("authorId", event.target.value)
                  }
                >
                  {AUTHOR_OPTIONS.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-3">
                <label className="form-label">
                  Custom Author Name (Optional)
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.authorName}
                  onChange={(event) =>
                    updateFormData("authorName", event.target.value)
                  }
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Related Blogs</label>
                <Select
                  isMulti
                  options={relatedBlogOptions}
                  value={relatedBlogOptions.filter((item) =>
                    formData.relatedBlogs.includes(item.value),
                  )}
                  onChange={(selected) =>
                    updateFormData(
                      "relatedBlogs",
                      Array.isArray(selected)
                        ? selected.map((item) => item.value)
                        : [],
                    )
                  }
                />
              </div>

              <div className="mb-3">
                <label className="form-label">
                  Featured Image (recommended 1200x630)
                </label>
                <input
                  type="text"
                  className="form-control mb-2"
                  placeholder="Paste image URL or upload below"
                  value={formData.featuredImage}
                  onChange={(event) =>
                    updateFormData("featuredImage", event.target.value)
                  }
                />
                <input
                  type="file"
                  className="form-control mb-3"
                  accept="image/*"
                  onChange={(event) =>
                    handleImageChange(event, "featuredImage")
                  }
                />
                <div className="avatar-preview mb-2">
                  <div
                    style={{
                      width: "100%",
                      minHeight: "180px",
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      borderRadius: "8px",
                      backgroundImage: `url(${formData.featuredImage || NoImage})`,
                    }}
                  />
                </div>
              </div>

              <div className="d-grid">
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-primary w-50"
                    onClick={() => handleSubmitBlog("draft")}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Saving..." : "Save Draft"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary w-50"
                    onClick={() => handleSubmitBlog()}
                    disabled={isSubmitting}
                  >
                    {isSubmitting
                      ? isEditMode
                        ? "Updating..."
                        : "Publishing..."
                      : isEditMode
                        ? "Update Blog"
                        : "Publish Blog"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {errorMessage ? (
            <div className="alert alert-danger" role="alert">
              {errorMessage}
            </div>
          ) : null}
          {successMessage ? (
            <div className="alert alert-success" role="alert">
              {successMessage}
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default AddBlog;
